import React from 'react';
import { useBrand } from '../../../app/config/brand';

interface ChoiceGroupProps extends React.FieldsetHTMLAttributes<HTMLFieldSetElement> {
  /** The question the group answers — rendered as the legend, so it is
      announced together with the options instead of the options alone. */
  question: string;
  children: React.ReactNode;
  /** Render the legend sr-only, for groups whose question carries no visible
      text on screen today. The name still computes from the legend. */
  hideLegend?: boolean;
  /** Marks the question with the required asterisk; kept aria-hidden so the
      asterisk never enters the group's accessible name. */
  required?: boolean;
  className?: string;
  /** Replaces the visible legend's default `mb-3`, for sites whose question
      element sat tighter against its options row. */
  legendClassName?: string;
}

/** The one wrapper for a choice group: a <fieldset> whose <legend> is the
    question. Native group labelling needs no id plumbing, and the chrome
    reset below keeps the converted layouts identical to the bare wrappers
    they replace. */
export const ChoiceGroup: React.FC<ChoiceGroupProps> = ({
  question,
  children,
  hideLegend = false,
  required = false,
  className = '',
  legendClassName,
  ...fieldsetProps
}) => {
  const brand = useBrand();
  return (
    <fieldset className={`border-0 p-0 m-0 min-w-0 ${className}`} {...fieldsetProps}>
      <legend
        className={hideLegend ? 'sr-only' : `block text-sm font-medium ${legendClassName ?? 'mb-3'}`}
        style={{ color: brand.colors.text }}
      >
        {required && (
          <span aria-hidden="true" style={{ color: brand.colors.error }}>* </span>
        )}
        {question}
      </legend>
      {children}
    </fieldset>
  );
};
